import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle, debounce } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  #revealed = 0;

  #shownRevealed = 0;


  /** L'en-tete part-il de l'etat descendu ? Vrai des le premier defilement. */
  #prime = false;

  /** Hauteur de l'en-tete, tenue a jour par le ResizeObserver — jamais relue
   *  pendant le defilement, une lecture par image couterait un recalcul. */
  #height = 0;
  /* Vrai des que l'en-tete est passee en `position: sticky` au moins une fois. */
  #pinned = false;
  /* Le prochain retrait doit etre pose sec, sans transition. */
  #withoutTransition = false;

  /** @type {HTMLElement | null} */
  #section = null;

  /** Derniere valeur publiee, pour n'ecrire que sur changement. */
  /** @type {string | null} */
  #publishedRevealed = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
    this.#height = roundedHeaderHeight;

    /*
     * AMORCAGE — publier l'etat « au repos » avant tout defilement.
     *
     * `--header-bottom` restait vide tant que l'en-tete n'avait pas defile une
     * premiere fois : les rangees epinglees retombaient sur la valeur de repli
     * (0px) et s'accrochaient trop haut, mesure a 43px au lieu de suivre le
     * convoi. On publie donc l'etat descendu des que la hauteur est connue.
     */
    if (this.#publishedRevealed === null) {
      this.#section ??= /** @type {HTMLElement | null} */ (this.closest('.header-section'));
      this.#section?.style.setProperty('--header-shift', '0px');
      document.body.style.setProperty('--header-bottom', `${roundedHeaderHeight - 1}px`);
    }

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Recalcule `data-menu-style` au redimensionnement.
   */
  #syncMenuStyle = () => {
    setHeaderMenuStyle();
    if (window.matchMedia('(pointer: coarse)').matches) {
      this.dataset.menuStyle = 'drawer';
    }
  };

  #resizeMenuStyleListener = debounce(this.#syncMenuStyle, 100);

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  /* Publie la part revelee, de 0 a 1. Le CSS en tire un `translate` : la
     hauteur vient de `100%`, donc rien a mesurer ici. */



  #publishRevealed = () => {
    /*
     * SEUL L'EN-TETE SE RETIRE — les rangees epinglees restent en place.
     *
     * Faire voyager l'en-tete et les rangees ensemble emporterait les filtres hors
     * de l'ecran des qu'on descend — exactement ce que le reglage « Filtres fixes »
     * sert a empecher. Les rangees s'epinglent donc sous le bord bas de l'en-tete et
     * y restent, meme quand il est retire. (L'animation d'apparition demandee portait
     * sur les SECTIONS : voir card-reveals.js.)
     */
    const total = this.#height;

    /*
     * SUIVI CONTINU, VOLONTAIREMENT CONSERVE.
     *
     * Une bascule binaire avec transition CSS — le modele d'alaena.com — est plus
     * fluide a l'oeil, mais elle DECOUPLE l'en-tete de ce qu'elle entraine : le bord
     * bas publie ci-dessous saute d'un coup tandis que l'en-tete met 0,2s a bouger.
     * Les rangees epinglees (sous-collections, filtres) partaient alors AVANT elle,
     * et la descente se figeait jusqu'au dernier pixel. Essaye, mesure, retire.
     */
    const part = total > 0 ? Math.min(1, this.#shownRevealed / total) : 1;
    const value = part.toFixed(4);
    if (value === this.#publishedRevealed) return;
    this.#publishedRevealed = value;

    /* Decalage du convoi, en pixels : 0 descendu, -total remonte. */
    const offset = -(1 - part) * total;
    this.#section ??= /** @type {HTMLElement | null} */ (this.closest('.header-section'));
    this.#section?.style.setProperty('--header-shift', `${offset.toFixed(2)}px`);

    /*
     * Le BORD BAS de l'en-tete, en pixels, publie dans la MEME image que son
     * deplacement. Les rangees epinglees en dessous (sous-collections, filtres)
     * s'en servent au lieu de le mesurer chacune dans sa propre image : mesure
     * avant, elles trainaient jusqu'a 35px derriere lui et le chevauchaient
     * pendant la revelation. Un seul ecrivain, une seule verite.
     *
     * `-1px` : l'en-tete s'epingle a `top: -1px` pour que l'IntersectionObserver
     * du theme puisse suivre son etat.
     */
    document.body.style.setProperty('--header-bottom', `${(this.#height - 1 + offset).toFixed(2)}px`);
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (!this.#prime) {
      this.#prime = true;
      // Au chargement l'en-tete est deja en place : rien a rattraper.
      this.#revealed = this.#height || this.offsetHeight;
      this.#shownRevealed = this.#revealed;
    }


    /*
     * Le retour est plus rapide que le depart : en remontant, l'en-tete avance
     * de 1,6 pixel par pixel defile. Le geste reste le pilote — c'est ce qui
     * fait la douceur — mais on n'a pas a remonter toute sa hauteur pour le
     * revoir entierement.
     *
     * Etait a 2 ; ramene a 1,6, le retour restant juge un peu vif. La course
     * complete passe de 38 a 48px de defilement sur ordinateur, de 61 a 76px
     * sur mobile.
     *
     * La disparition, elle, reste au 1:1 : accelerer aussi la sortie ferait
     * fuir l'en-tete au moindre mouvement vers le bas.
     */
    const RETURN_SPEED = 1.6;
    const gap = this.#lastScrollTop - scrollTop;
    const delta = gap > 0 ? gap * RETURN_SPEED : gap;
    const height = this.#height || this.offsetHeight;

    if (isAtTop) {
      // reset sticky state when header is scrolled up to natural position
      this.#revealed = height;
      this.#offscreen = false;
      this.#pinned = false;
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
    } else if (gap < 0 && !this.#pinned) {
      /*
       * ON QUITTE LE HAUT DE PAGE EN DESCENDANT : rien a retirer, l'en-tete s'en va
       * avec la page. Sans ce cas, on entrait dans la branche collante avec `#revealed`
       * encore plein et `position: sticky` ramenait a l'ecran une en-tete deja sortie
       * (mesure : bord bas de -6 a +75, 81 px d'un coup).
       *
       * Deux pieges dans la condition :
       * - `!isScrollingUp` ne suffit pas : a l'arret `scrollTop === lastScrollTop`, donc
       *   il est vrai et l'en-tete revelee se retirait des qu'on lachait le defilement.
       *   D'ou le mouvement vers le bas REEL (`ecart < 0`).
       * - on teste `#pinned`, pas `dataset.stickyState`, absent au tout premier
       *   defilement — la comparaison echouait et le sursaut revenait.
       */
      this.#revealed = 0;
      this.#shownRevealed = 0;

      /*
       * On publie le retrait AVANT de passer l'etat, dans la meme image. L'etat
       * declenche `position: sticky` cote CSS ; si `--header-shift` valait encore
       * 0, l'en-tete etait epinglee a `top: -1px`, donc visible, l'image d'avant que
       * la boucle d'animation ne la retire. C'etait ca, le bond de 81 pixels.
       */
      /*
       * SANS TRANSITION ICI. L'en-tete est deja sortie de l'ecran avec la page : il n'y
       * a rien a animer. En laissant courir la transition, `position: sticky` prenait
       * effet alors que le retrait n'avait parcouru que 8px sur 76 — l'en-tete etait
       * epinglee VISIBLE puis glissait vers le haut. Mesure : +73px. Le retrait doit
       * etre pose sec, la transition ne sert qu'aux mouvements reellement visibles.
       */
      this.#withoutTransition = true;

      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
      this.#pinned = true;
    } else {
      /*
       * Valeur d'ARRIVEE, plus un suivi du doigt. Le suivi proportionnel dependait de
       * la cadence des evenements de defilement — la source meme des paliers sur
       * appareil tactile. On se montre en remontant, on se retire en descendant, et
       * la duree est desormais la meme partout.
       */
      this.#revealed = gap > 0 ? height : 0;

      if (this.#revealed > 0) {
        this.#pinned = true;
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = isScrollingUp ? 'up' : 'down';
      } else {
        this.dataset.scrollDirection = 'none';
        this.dataset.stickyState = 'idle';
      }
    }

    /*
     * Une seule ecriture par changement d'etat : la valeur d'arrivee. L'interpolation
     * est faite par le navigateur (cf. `@property` dans base.css), pas ici.
     */
    this.#shownRevealed = this.#revealed;

    if (this.#withoutTransition) {
      this.#withoutTransition = false;
      document.documentElement.removeAttribute('data-header-animated');
      this.#publishRevealed();
      // Rendue a l'image suivante, une fois la valeur appliquee sans animation.
      requestAnimationFrame(() => document.documentElement.setAttribute('data-header-animated', ''));
    } else {
      document.documentElement.setAttribute('data-header-animated', '');
      this.#publishRevealed();
    }
    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#updateCartCounter);
    window.addEventListener('resize', this.#resizeMenuStyleListener);
    this.#syncMenuStyle();

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  /** @param {Event & {promise?: Promise<{cart?: {totalQuantity?: number}}>}} event */
  #updateCartCounter = async (event) => {
    /** @param {unknown} count */
    const write = (count) => {
      if (typeof count !== 'number') return;
      for (const counter of document.querySelectorAll('[data-cart-count]')) {
        if (!(counter instanceof HTMLElement)) continue;
        counter.textContent = String(count);
        // Le style masque la pastille à zéro : l'attribut suit le texte.
        counter.dataset.count = String(count);
      }
    };

    const reread = async () => {
      try {
        const response = await fetch(`${Theme.routes.cart_url}.js`);
        const cart = await response.json();
        write(cart?.item_count);
      } catch {}
    };

    if (!event?.promise) {
      await reread();
      return;
    }

    try {
      const { cart } = await event.promise;
      if (typeof cart?.totalQuantity === 'number') write(cart.totalQuantity);
      else await reread();
    } catch {}
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#updateCartCounter);
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    window.removeEventListener('resize', this.#resizeMenuStyleListener);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

// Anchor the shopify-account dialog to the bottom of the account button itself.
document.addEventListener(
  'open',
  (event) => {
    const { target } = event;
    if (!(target instanceof HTMLElement) || !target.matches('shopify-account')) return;

    const bottom = Math.max(0, Math.round(target.getBoundingClientRect().bottom));
    target.style.setProperty('--account-dialog-top', `${bottom}px`);
  },
  true
);

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
